import api from "@/core/api/client";

export interface CompanyUsageRow {
  companyId: string;
  companyName: string | null;
  messagesIn: number;
  messagesOut: number;
  messagesTotal: number;
  mediaCount: number;
  conversationsNew: number;
  conversationsActive: number;
  /** Base total de contatos da empresa — não só os criados no período. */
  contactsTotal: number;
  contactsNew: number;
  /** Espaço ocupado no Blob. Foto do agora, não acúmulo do período. */
  storageBytes: number;
  storageFiles: number;
  /**
   * Estimativa de espaço em banco: linhas da empresa × tamanho médio da tabela.
   * É rateio, não medição — o Postgres não separa bytes por inquilino. Serve
   * para comparar ordens de grandeza, não para cobrar por byte.
   */
  dbBytes: number;
  scheduledTotal: number;
  /** Já disparados — o trabalho que o agendamento de fato gerou. */
  scheduledSent: number;
  /** Ainda na fila — consumo contratado, ainda não realizado. */
  scheduledPending: number;
  /** Fatia da empresa no volume do período, em pontos percentuais. */
  shareOfMessages: number;
}

export interface UsageMeta {
  from: string;
  to: string;
  totalCompanies: number;
}

export interface UsageSeriesPoint {
  day: string;
  messagesTotal: number;
  mediaCount: number;
}

export interface UsageRangeParams {
  /** `YYYY-MM-DD`, inclusive. */
  from?: string;
  to?: string;
  companyId?: string;
}

function buildQuery(params: UsageRangeParams): string {
  const search = new URLSearchParams();
  if (params.from) search.set("from", params.from);
  if (params.to) search.set("to", params.to);
  if (params.companyId) search.set("companyId", params.companyId);
  const q = search.toString();
  return q ? `?${q}` : "";
}

export const usageService = {
  /** Ranking de consumo por empresa no período. */
  async byCompany(
    params: UsageRangeParams,
  ): Promise<{ data: CompanyUsageRow[]; meta: UsageMeta }> {
    const res = await api.get<{
      success: boolean;
      data: CompanyUsageRow[];
      meta: UsageMeta;
    }>(`/usage/by-company${buildQuery(params)}`);
    if (!res.data.success)
      throw new Error("Erro ao buscar consumo por empresa");
    return { data: res.data.data, meta: res.data.meta };
  },

  /** Série diária. Sem `companyId`, é o total da plataforma. */
  async series(params: UsageRangeParams): Promise<UsageSeriesPoint[]> {
    const res = await api.get<{ success: boolean; data: UsageSeriesPoint[] }>(
      `/usage/series${buildQuery(params)}`,
    );
    if (!res.data.success) throw new Error("Erro ao buscar série de consumo");
    return res.data.data;
  },
};
